import React, { useMemo } from 'react';
import { Text, TouchableOpacity, View, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { FeedEntry, isAlertEntry } from './feedItems';
import type { ActionItem } from '../../services/api/actionsApi';

interface FeedItemProps {
  entry: FeedEntry;
  onPress: (entry: FeedEntry) => void;
  /** Alert rows only: 复盘 thesis shortcut (T7). */
  onReviewThesis?: (entry: FeedEntry) => void;
  /** Every row: 💬 讨论 → new session seeded with a discuss_item command (T20). */
  onDiscuss?: (entry: FeedEntry) => void;
}

/**
 * One Feed row: unread dot (read_at) · type glyph · title + meta, plus the
 * row actions — 💬 讨论 on every row (T20) and the 复盘 thesis shortcut on
 * 🔺 alert rows (T7). Read-mostly by design (spec §5) — tap opens/marks read.
 */
export function FeedItem({ entry, onPress, onReviewThesis, onDiscuss }: FeedItemProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const symbol = (entry.item as ActionItem).symbol;

  return (
    <TouchableOpacity
      style={styles.row}
      testID={`feed-row-${entry.key}`}
      onPress={() => onPress(entry)}
    >
      <View style={styles.dotCol}>
        {entry.unread && <View style={styles.dot} testID="feed-unread-dot" />}
      </View>
      <Text style={styles.icon}>{entry.icon}</Text>
      <View style={styles.contentCol}>
        <Text style={[styles.title, entry.unread && styles.titleUnread]} numberOfLines={1}>
          {entry.title}
        </Text>
        {!!entry.meta && (
          <Text style={styles.meta} numberOfLines={2}>
            {entry.meta}
          </Text>
        )}
      </View>
      {onDiscuss && (
        <TouchableOpacity
          testID={`feed-discuss-${entry.key}`}
          style={styles.discussBtn}
          onPress={() => onDiscuss(entry)}
          accessibilityRole="button"
          accessibilityLabel={`${t('feed.discuss', { defaultValue: 'Discuss' })}: ${entry.title}`}
        >
          <Text style={styles.discussBtnText}>
            {`💬 ${t('feed.discuss', { defaultValue: 'Discuss' })}`}
          </Text>
        </TouchableOpacity>
      )}
      {isAlertEntry(entry) && (
        <TouchableOpacity
          testID={`feed-review-thesis-${entry.key}`}
          style={[styles.reviewBtn, !symbol && styles.reviewBtnDisabled]}
          onPress={() => onReviewThesis?.(entry)}
          disabled={!symbol}
          accessibilityRole="button"
          accessibilityState={{ disabled: !symbol }}
        >
          <Text style={[styles.reviewBtnText, !symbol && styles.reviewBtnTextDisabled]}>
            {t('feed.reviewThesis', { defaultValue: 'Review thesis' })}
          </Text>
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
}

function createStyles(theme: any) {
  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      paddingVertical: theme.spacing.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    dotCol: { width: 14, alignItems: 'center', justifyContent: 'center' },
    dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: theme.colors.primary },
    icon: { width: 26, fontSize: theme.typography.fontSizeMd, paddingTop: 1 },
    contentCol: { flex: 1 },
    title: { fontSize: theme.typography.fontSizeMd, color: theme.colors.text },
    titleUnread: { fontWeight: theme.typography.fontWeightBold },
    meta: {
      marginTop: 2,
      fontSize: theme.typography.fontSizeSm,
      color: theme.colors.textSecondary,
    },
    reviewBtn: {
      marginLeft: theme.spacing.sm,
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.primary,
      alignSelf: 'center',
    },
    reviewBtnText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.primary,
    },
    // 💬 讨论 (T20): same outline treatment as the 复盘 shortcut — a row
    // action, never a second tap target ambiguity with the row itself.
    discussBtn: {
      marginLeft: theme.spacing.sm,
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.primary,
      alignSelf: 'center',
    },
    discussBtnText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.primary,
    },
    // Symbol-less alert: the shortcut stays visible but muted — subtle hint
    // that review isn't available here, never a navigation with null symbol.
    reviewBtnDisabled: { borderColor: theme.colors.border },
    reviewBtnTextDisabled: { color: theme.colors.textTertiary },
  });
}
